import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import DateRangeFilter from './DateRangeFilter';
import { formatCapacity } from '@/lib/utils/format';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface StorageChartProps {
  data: any;
}

const StorageChart: React.FC<StorageChartProps> = ({ data }) => {
  const [showForecast, setShowForecast] = useState(true);
  const [forecastType, setForecastType] = useState<'netapp' | 'linear_trend'>('netapp');

  // Calculate min and max dates from HISTORICAL data only (for date filter)
  const trendData = data?.trend || [];
  const minDate = trendData.length > 0
    ? new Date(Math.min(...trendData.map((d: any) => new Date(d.date).getTime())))
    : new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
  const maxDate = trendData.length > 0
    ? new Date(Math.max(...trendData.map((d: any) => new Date(d.date).getTime())))
    : new Date();

  // Get today's date for more intuitive presets
  const today = new Date();

  // Initialize date range to show all historical data by default
  const [dateRange, setDateRange] = useState({
    start: minDate,
    end: maxDate
  });

  const handleDateRangeChange = (start: Date, end: Date) => {
    setDateRange({ start, end });
  };

  // Filter ONLY historical data based on date range
  // Forecast should always show all available data when enabled
  const filteredTrend = (data?.trend || []).filter((item: any) => {
    const itemDate = new Date(item.date);
    return itemDate >= dateRange.start && itemDate <= dateRange.end;
  });

  // Get forecast data based on selected type
  // Support both old format (array) and new format (object with netapp/linear_trend)
  const getForecastData = () => {
    if (!showForecast || !data?.forecast) return [];

    // Check if forecast is the new object format
    if (typeof data.forecast === 'object' && !Array.isArray(data.forecast)) {
      // New format with separate forecast types
      return data.forecast[forecastType] || [];
    }

    // Old format (direct array) - fallback for backward compatibility
    return data.forecast || [];
  };

  const forecastToShow = getForecastData();

  // Combine filtered historical and full forecast data
  const allDataPoints = [...filteredTrend, ...forecastToShow];

  // Create labels from filtered data points
  const labels = allDataPoints.map((item: any) => {
    const date = new Date(item.date);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  });

  // Separate historical and forecast values
  const historicalData = filteredTrend.map((item: any) => item.value);
  const forecastData = forecastToShow.map((item: any) => item.value);

  // Create null-padded arrays for proper visualization
  const historicalValues = [...historicalData, ...Array(forecastData.length).fill(null)];
  const forecastValues = showForecast ? [...Array(historicalData.length).fill(null), ...forecastData] : [];

  const datasets = [
    {
      label: 'Historical Usage (%)',
      data: historicalValues,
      borderColor: 'rgb(99, 102, 241)',
      backgroundColor: 'rgba(99, 102, 241, 0.1)',
      fill: true,
      tension: 0.4,
      spanGaps: false
    }
  ];

  if (showForecast && forecastValues.length > 0) {
    const forecastLabel = forecastType === 'linear_trend' ? 'Forecast (%)' : 'Forecast (%)';
    datasets.push({
      label: forecastLabel,
      data: forecastValues,
      borderColor: 'rgb(251, 146, 60)', // Brighter orange for better visibility on dark backgrounds
      backgroundColor: 'rgba(251, 146, 60, 0.05)',
      borderDash: [5, 5] as any,  // TypeScript workaround for Chart.js type
      fill: true,
      tension: 0.4,
      spanGaps: false
    } as any);
  }

  const chartData = {
    labels: labels,
    datasets: datasets
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          boxWidth: 6,
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      title: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        borderRadius: 8,
        titleFont: {
          size: 14
        },
        bodyFont: {
          size: 13
        },
        callbacks: {
          title: function(context: any) {
            // Show month and year in tooltip title (no day)
            const dataPoint = allDataPoints[context[0]?.dataIndex];
            if (dataPoint?.date) {
              const date = new Date(dataPoint.date);
              return date.toLocaleDateString('en-US', {
                month: 'short',
                year: 'numeric'
              });
            }
            return '';
          },
          label: function(context: any) {
            if (context.parsed.y !== null) {
              const label = context.dataset.label || '';
              const value = context.parsed.y.toFixed(1);
              const dataPoint = allDataPoints[context.dataIndex];
              if (dataPoint?.used_tb !== undefined && dataPoint?.allocated_tb !== undefined) {
                const lines = [
                  `${label}: ${value}%`,
                  `Used: ${formatCapacity(dataPoint.used_tb)}`,
                  `Allocated: ${formatCapacity(dataPoint.allocated_tb)}`
                ];

                // Add system count if available (only for historical data)
                if (dataPoint.system_count !== undefined && dataPoint.total_systems !== undefined) {
                  const systemCount = dataPoint.system_count;
                  const totalSystems = dataPoint.total_systems;
                  if (systemCount < totalSystems) {
                    lines.push(`Systems: ${systemCount}/${totalSystems} reported`);
                  } else {
                    lines.push(`Systems: ${systemCount}/${totalSystems}`);
                  }
                }

                if (dataPoint.interpolated) {
                  lines.push('(Interpolated)');
                }
                return lines;
              }
              return `${label}: ${value}%`;
            }
            return undefined;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#6B7280'
        }
      },
      y: {
        min: 0,
        max: 100,
        ticks: {
          color: '#6B7280',
          callback: function(value: any) {
            return value + '%';
          }
        },
        grid: {
          color: 'rgba(107, 114, 128, 0.1)'
        }
      }
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800/95 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 h-full min-h-[400px]">
      <div className="mb-6">
        {/* Title and metrics row */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Storage Trend & Forecast
          </h3>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-600 dark:text-gray-400">Current:</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {data?.summary?.usage_percentage || 0}%
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600 dark:text-gray-400">Available:</span>
              <span className="font-semibold text-green-600 dark:text-green-400">
                {formatCapacity(data?.summary?.total_free_tb)}
              </span>
            </div>
          </div>
        </div>

        {/* Controls row */}
        <div className="flex justify-between items-center">
          <DateRangeFilter
            startDate={dateRange.start}
            endDate={dateRange.end}
            onChange={handleDateRangeChange}
            minDate={minDate}
            maxDate={maxDate}
            presets={[
              {
                label: 'Last 30 days',
                getValue: () => ({
                  start: new Date(Math.max(minDate.getTime(), today.getTime() - 30 * 24 * 60 * 60 * 1000)),
                  end: new Date(Math.min(today.getTime(), maxDate.getTime()))
                })
              },
              {
                label: 'Last 3 months',
                getValue: () => ({
                  start: new Date(Math.max(minDate.getTime(), today.getTime() - 90 * 24 * 60 * 60 * 1000)),
                  end: new Date(Math.min(today.getTime(), maxDate.getTime()))
                })
              },
              {
                label: 'Last 6 months',
                getValue: () => ({
                  start: new Date(Math.max(minDate.getTime(), today.getTime() - 180 * 24 * 60 * 60 * 1000)),
                  end: new Date(Math.min(today.getTime(), maxDate.getTime()))
                })
              },
              {
                label: 'Last year',
                getValue: () => ({
                  start: new Date(Math.max(minDate.getTime(), today.getTime() - 365 * 24 * 60 * 60 * 1000)),
                  end: new Date(Math.min(today.getTime(), maxDate.getTime()))
                })
              },
              {
                label: 'All historical',
                getValue: () => ({
                  start: minDate,
                  end: maxDate
                })
              }
            ]}
          />

          {/* Forecast controls */}
          {((data?.forecast?.netapp && data.forecast.netapp.length > 0) ||
            (data?.forecast?.linear_trend && data.forecast.linear_trend.length > 0) ||
            (Array.isArray(data?.forecast) && data.forecast.length > 0)) && (
            <div className="flex items-center gap-4">
              {/* Forecast type selector */}
              {data?.forecast?.linear_trend && data.forecast.linear_trend.length > 0 && (
                <select
                  value={forecastType}
                  onChange={(e) => setForecastType(e.target.value as 'netapp' | 'linear_trend')}
                  className="text-sm px-3 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  disabled={!showForecast}
                >
                  <option value="linear_trend">Linear Trend Forecast</option>
                  <option value="netapp">NetApp API Forecast</option>
                </select>
              )}

              {/* Forecast toggle */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Show Forecast ({forecastToShow.length} months)
                </span>
                <button
                  onClick={() => setShowForecast(!showForecast)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    showForecast ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                  aria-label="Toggle forecast"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      showForecast ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
      <div className="h-64">
        {allDataPoints.length > 0 ? (
          <>
            <Line data={chartData} options={options} />
            {/* Show data information */}
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center">
              {filteredTrend.length < (data?.trend || []).length ? (
                <>
                  Showing {filteredTrend.length} of {(data?.trend || []).length} historical points
                  {showForecast && forecastToShow.length > 0 &&
                    ` and ${forecastToShow.length} forecast points (${forecastType === 'linear_trend' ? 'Linear Trend' : 'NetApp API'})`}
                </>
              ) : (
                <>
                  Data is collected monthly • Current usage: {data?.summary?.usage_percentage || 0}%
                  {showForecast && forecastToShow.length > 0 &&
                    ` • Forecast: ${forecastType === 'linear_trend' ? 'Linear Trend' : 'NetApp API'}`}
                </>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
            No data available for the selected date range
          </div>
        )}
      </div>
    </div>
  );
};

export default StorageChart;